import { useEffect, useMemo, useRef, useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Library, Plus, Pencil, Trash2, Eye, EyeOff, CheckCircle2, XCircle,
  Upload, Loader2, ShieldCheck, X, Users, UserPlus, Sparkles,
} from "lucide-react";
import FacilitatorConsoleModal from "@/components/FacilitatorConsoleModal";

const MODE_LABELS = {
  self_paced: "Self-paced",
  live_group: "Live group",
  one_to_one: "One-to-one",
};
const LANGS = [
  ["en", "English"], ["hi", "हिन्दी"], ["ta", "தமிழ்"], ["te", "తెలుగు"],
  ["kn", "ಕನ್ನಡ"], ["mr", "मराठी"], ["bn", "বাংলা"],
];
const BANDS = ["normal", "mild", "moderate", "high"];

export default function DatpAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("content");
  const isAdmin = user?.role === "platform_admin";

  return (
    <div className="space-y-8" data-testid="datp-admin-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="uppercase-label inline-flex items-center gap-1">
            <Library size={14} /> Content library
          </span>
          <h1 className="font-display text-3xl mt-2">Digital Adjunctive Therapy</h1>
          <p className="text-[color:var(--mano-ink-soft)] mt-1 max-w-2xl text-sm">
            Add therapies whenever you're ready — save as draft, publish when the content is complete,
            or flip the master switch to hide anything without deleting it.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-[color:var(--mano-border)] bg-white p-1">
          <TabBtn active={tab === "content"} onClick={() => setTab("content")} testid="tab-content">
            Content
          </TabBtn>
          {isAdmin && (
            <TabBtn active={tab === "subadmins"} onClick={() => setTab("subadmins")} testid="tab-subadmins">
              Sub-admins
            </TabBtn>
          )}
        </div>
      </div>

      {tab === "content" ? <ContentTab /> : <SubAdminsTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-1.5 text-sm rounded-full font-semibold transition-colors ${
        active ? "bg-[color:var(--mano-primary)] text-white" : "text-[color:var(--mano-ink-soft)]"
      }`}
    >
      {children}
    </button>
  );
}

// ------------------------------ Content Tab ------------------------------
function ContentTab() {
  const [cats, setCats] = useState([]);
  const [therapies, setTherapies] = useState([]);
  const [filterCat, setFilterCat] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | therapy object
  const [console_tid, setConsoleTid] = useState(null);
  const [adherence, setAdherence] = useState(null);

  const refresh = () => {
    api.get("/datp/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/datp/adherence").then((r) => setAdherence(r.data)).catch(() => {});
    const params = new URLSearchParams();
    if (filterCat) params.set("category", filterCat);
    if (filterStatus) params.set("status", filterStatus);
    api.get(`/admin/datp/therapies?${params.toString()}`)
      .then((r) => setTherapies(r.data))
      .catch(() => {});
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterCat, filterStatus]);

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/datp/therapies/${t.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(extractError(e)); }
  };
  const togglePublish = async (t) => {
    try {
      await api.post(`/admin/datp/therapies/${t.id}/${t.published ? "unpublish" : "publish"}`);
      toast.success(t.published ? "Unpublished" : "Published");
      refresh();
    } catch (e) { toast.error(extractError(e)); }
  };
  const toggleEnabled = async (t) => {
    try {
      await api.post(`/admin/datp/therapies/${t.id}/toggle`);
      refresh();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-4">
      {adherence && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="datp-adherence">
          <StatCard label="Unique users" value={adherence.unique_users} />
          <StatCard label="Sessions opened" value={adherence.total_opens} />
          <StatCard label="Completions" value={adherence.total_completions} />
          <StatCard label="Completion rate" value={`${adherence.completion_rate}%`} />
        </div>
      )}
    <div className="grid lg:grid-cols-[220px_1fr] gap-6">
      {/* Category tree */}
      <aside className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-3" data-testid="datp-category-tree">
        <button
          onClick={() => setFilterCat(null)}
          className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
            !filterCat ? "bg-[color:var(--mano-primary)] text-white" : "hover:bg-[color:var(--mano-bg)]"
          }`}
        >
          All categories <span className="opacity-70 text-xs">({cats.reduce((n, c) => n + c.published_count, 0)})</span>
        </button>
        <div className="mt-1 space-y-0.5 max-h-[420px] overflow-y-auto">
          {cats.map((c) => (
            <button
              key={c.key}
              data-testid={`cat-${c.key}`}
              onClick={() => setFilterCat(c.key)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                filterCat === c.key
                  ? "bg-[color:var(--mano-primary)] text-white"
                  : "hover:bg-[color:var(--mano-bg)] text-[color:var(--mano-ink)]"
              }`}
            >
              <div className="font-medium">{c.name}</div>
              <div className={`text-[11px] ${filterCat === c.key ? "text-white/80" : "text-[color:var(--mano-ink-soft)]"}`}>
                {c.published_count} live
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Therapies list */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="inline-flex rounded-full border border-[color:var(--mano-border)] bg-white p-1 text-sm">
            {[["", "All"], ["draft", "Drafts"], ["published", "Published"], ["disabled", "Disabled"]].map(([v, l]) => (
              <button
                key={v || "all"}
                onClick={() => setFilterStatus(v)}
                data-testid={`filter-${v || "all"}`}
                className={`px-3 py-1 rounded-full font-medium ${
                  filterStatus === v ? "bg-[color:var(--mano-ink)] text-white" : "text-[color:var(--mano-ink-soft)]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="datp-new-btn"
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-sm font-semibold transition-colors"
          >
            <Plus size={14} /> New therapy
          </button>
        </div>

        {therapies.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-8 text-center text-[color:var(--mano-ink-soft)]">
            No therapies yet. Add your first one — it stays as a draft until you hit Publish.
          </div>
        ) : (
          <div className="space-y-2" data-testid="datp-therapies-list">
            {therapies.map((t) => (
              <div key={t.id} data-testid={`row-${t.id}`} className="rounded-xl bg-white border border-[color:var(--mano-border)] p-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{t.title}</span>
                    <StatusPill published={t.published} />
                    {!t.enabled && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">DISABLED</span>
                    )}
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] font-semibold">
                      {MODE_LABELS[t.mode] || t.mode}
                    </span>
                    <span className="text-[10px] uppercase text-[color:var(--mano-ink-soft)]">{t.language}</span>
                  </div>
                  <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5">
                    {t.category_name}{t.subcategory ? " · " + t.subcategory : ""}
                    {t.duration_min ? ` · ${t.duration_min} min` : ""}
                    {t.media_type ? ` · ${t.media_type}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {t.mode === "live_group" && (
                    <IconBtn testid={`fc-open-${t.id}`} title="Facilitator console" onClick={() => setConsoleTid(t.id)}>
                      <Sparkles size={15} className="text-[color:var(--mano-primary)]" />
                    </IconBtn>
                  )}
                  <IconBtn testid={`toggle-enabled-${t.id}`} title={t.enabled ? "Hide from users" : "Show to users"} onClick={() => toggleEnabled(t)}>
                    {t.enabled ? <Eye size={15} /> : <EyeOff size={15} />}
                  </IconBtn>
                  <IconBtn testid={`toggle-publish-${t.id}`} title={t.published ? "Unpublish" : "Publish"} onClick={() => togglePublish(t)}>
                    {t.published ? <XCircle size={15} className="text-amber-600" /> : <CheckCircle2 size={15} className="text-emerald-600" />}
                  </IconBtn>
                  <IconBtn testid={`edit-${t.id}`} title="Edit" onClick={() => setEditing(t)}>
                    <Pencil size={15} />
                  </IconBtn>
                  <IconBtn testid={`delete-${t.id}`} title="Delete" onClick={() => del(t)}>
                    <Trash2 size={15} className="text-rose-600" />
                  </IconBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing !== null && (
        <TherapyEditor
          initial={editing === "new" ? null : editing}
          cats={cats}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {console_tid && (
        <FacilitatorConsoleModal tid={console_tid} onClose={() => setConsoleTid(null)} />
      )}
    </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-4">
      <div className="uppercase-label">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ published }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
      published ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
    }`}>
      {published ? "PUBLISHED" : "DRAFT"}
    </span>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button
      data-testid={testid}
      title={title}
      onClick={onClick}
      className="h-8 w-8 rounded-lg border border-[color:var(--mano-border)] bg-white hover:bg-[color:var(--mano-bg)] flex items-center justify-center transition-colors"
    >
      {children}
    </button>
  );
}

// ------------------------------ Editor Modal ------------------------------
function TherapyEditor({ initial, cats, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    subtitle: initial?.subtitle || "",
    description: initial?.description || "",
    category_key: initial?.category_key || cats[0]?.key || "",
    subcategory: initial?.subcategory || "",
    mode: initial?.mode || "self_paced",
    language: initial?.language || "en",
    duration_min: initial?.duration_min || "",
    media_url: initial?.media_url || "",
    thumbnail_url: initial?.thumbnail_url || "",
    tags: (initial?.tags || []).join(", "),
    target_bands: initial?.target_bands || [],
    scheduled_at: initial?.scheduled_at || "",
    capacity: initial?.capacity || "",
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const currentCat = cats.find((c) => c.key === form.category_key);

  const save = async (publishAfter) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle || null,
        description: form.description || null,
        category_key: form.category_key,
        subcategory: form.subcategory || null,
        mode: form.mode,
        language: form.language,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        media_url: form.media_url || null,
        thumbnail_url: form.thumbnail_url || null,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        target_bands: form.target_bands,
        scheduled_at: form.mode === "live_group" ? (form.scheduled_at || null) : null,
        capacity: form.mode === "live_group" && form.capacity ? Number(form.capacity) : null,
      };
      let id = initial?.id;
      if (isNew) {
        const r = await api.post("/admin/datp/therapies", payload);
        id = r.data.id;
      } else {
        await api.put(`/admin/datp/therapies/${id}`, payload);
      }
      if (publishAfter) {
        await api.post(`/admin/datp/therapies/${id}/publish`);
        toast.success("Published — patients can see it now.");
      } else {
        toast.success(isNew ? "Draft saved" : "Saved");
      }
      onSaved();
    } catch (e) {
      toast.error(extractError(e));
    } finally { setSaving(false); }
  };

  const upload = async (fileObj) => {
    if (!fileObj) return;
    if (isNew) {
      // Save as draft first so we have an id to attach media to
      toast.info("Saving draft first so we can attach the file…");
      await save(false);
      return; // user reopens edit and uploads
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const r = await api.post(`/admin/datp/therapies/${initial.id}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("media_url", r.data.media_url);
      toast.success(`Uploaded ${(r.data.size_bytes / 1024 / 1024).toFixed(1)} MB`);
    } catch (e) { toast.error(extractError(e)); }
    finally { setUploading(false); }
  };

  const canSubmit = form.title.trim() && form.category_key && form.mode;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="therapy-editor">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 lg:p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <span className="uppercase-label">{isNew ? "New therapy" : "Edit therapy"}</span>
            <h2 className="font-display text-2xl mt-1">{form.title || "Untitled therapy"}</h2>
          </div>
          <button onClick={onClose} className="p-1" data-testid="editor-close"><X size={18} /></button>
        </div>

        <div className="mt-5 space-y-4">
          <Field label="Title">
            <input required data-testid="ed-title" value={form.title} onChange={(e) => set("title", e.target.value)} className={inp} />
          </Field>
          <Field label="Short subtitle (optional)">
            <input data-testid="ed-subtitle" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} className={inp} />
          </Field>
          <Field label="Description">
            <textarea data-testid="ed-description" value={form.description} onChange={(e) => set("description", e.target.value)} className={`${inp} !h-24 py-3`} />
          </Field>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Category">
              <select data-testid="ed-category" value={form.category_key} onChange={(e) => set("category_key", e.target.value)} className={inp}>
                {cats.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Subcategory">
              <select data-testid="ed-subcategory" value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} className={inp}>
                <option value="">— none —</option>
                {(currentCat?.subcategories || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Mode">
              <select data-testid="ed-mode" value={form.mode} onChange={(e) => set("mode", e.target.value)} className={inp}>
                {Object.entries(MODE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Language">
              <select data-testid="ed-language" value={form.language} onChange={(e) => set("language", e.target.value)} className={inp}>
                {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Duration (min)">
              <input data-testid="ed-duration" type="number" min="0" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} className={inp} />
            </Field>
          </div>

          {form.mode === "live_group" && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Scheduled start (local time)">
                <input data-testid="ed-scheduled" type="datetime-local" value={form.scheduled_at?.slice(0, 16) || ""} onChange={(e) => set("scheduled_at", e.target.value ? new Date(e.target.value).toISOString() : "")} className={inp} />
              </Field>
              <Field label="Capacity">
                <input data-testid="ed-capacity" type="number" min="1" max="500" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className={inp} />
              </Field>
            </div>
          )}

          <Field label="Media URL (YouTube, Vimeo, direct MP4/MP3, PDF)">
            <input data-testid="ed-media-url" placeholder="https://…" value={form.media_url} onChange={(e) => set("media_url", e.target.value)} className={inp} />
          </Field>

          {!isNew && (
            <div className="rounded-xl border border-dashed border-[color:var(--mano-border)] p-4">
              <div className="text-sm font-semibold">Or upload the media file directly</div>
              <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5">Video (MP4/WEBM), Audio (MP3/M4A), or PDF up to 500 MB. It streams via the app.</div>
              <input ref={fileRef} type="file" hidden accept="video/*,audio/*,application/pdf" onChange={(e) => upload(e.target.files?.[0])} data-testid="ed-file-input" />
              <button
                data-testid="ed-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="mt-3 inline-flex items-center gap-1 px-4 py-2 rounded-full border border-[color:var(--mano-primary)] text-[color:var(--mano-primary)] text-sm font-semibold hover:bg-[color:var(--mano-primary)]/5 transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? "Uploading…" : "Upload file"}
              </button>
            </div>
          )}

          <Field label="Thumbnail URL (optional)">
            <input data-testid="ed-thumbnail" value={form.thumbnail_url} onChange={(e) => set("thumbnail_url", e.target.value)} className={inp} />
          </Field>

          <Field label="Tags (comma-separated)">
            <input data-testid="ed-tags" placeholder="anxiety, breathing, morning" value={form.tags} onChange={(e) => set("tags", e.target.value)} className={inp} />
          </Field>

          <Field label="Target bands (from voice biomarker score)">
            <div className="flex flex-wrap gap-1.5 pt-1">
              {BANDS.map((b) => {
                const on = form.target_bands.includes(b);
                return (
                  <button
                    key={b}
                    type="button"
                    data-testid={`ed-band-${b}`}
                    onClick={() => set("target_bands", on ? form.target_bands.filter((x) => x !== b) : [...form.target_bands, b])}
                    className={`px-3 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${
                      on ? "bg-[color:var(--mano-primary)] text-white" : "border border-[color:var(--mano-border)] text-[color:var(--mano-ink-soft)]"
                    }`}
                  >
                    {b}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 justify-end">
          <button
            data-testid="ed-save-draft"
            onClick={() => save(false)}
            disabled={!canSubmit || saving}
            className="px-5 py-2.5 rounded-full border border-[color:var(--mano-border)] font-semibold text-sm disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            data-testid="ed-save-publish"
            onClick={() => save(true)}
            disabled={!canSubmit || saving}
            className="px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold text-sm transition-colors disabled:opacity-60"
          >
            {saving ? "Publishing…" : "Save & publish"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------ Sub-admins Tab ------------------------------
function SubAdminsTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/admin/sub-admins").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/sub-admins", form);
      toast.success(`Sub-admin ${form.name} created.`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", phone: "" });
      refresh();
    } catch (e) { toast.error(extractError(e)); } finally { setBusy(false); }
  };

  const toggle = async (u) => {
    try {
      await api.post(`/admin/sub-admins/${u.id}/${u.active ? "deactivate" : "reactivate"}`);
      toast.success(u.active ? "Deactivated" : "Reactivated");
      refresh();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="subadmins-tab">
      <div className="flex items-center justify-between">
        <span className="uppercase-label inline-flex items-center gap-1"><Users size={14} /> Sub-admin accounts</span>
        <button
          data-testid="subadmin-new-btn"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-sm font-semibold transition-colors"
        >
          <UserPlus size={14} /> New sub-admin
        </button>
      </div>

      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-8 text-center text-[color:var(--mano-ink-soft)]">
          No sub-admins yet.
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((u) => (
            <div key={u.id} className="rounded-xl bg-white border border-[color:var(--mano-border)] p-4 flex items-center gap-4 flex-wrap" data-testid={`subadmin-${u.id}`}>
              <div className="h-10 w-10 rounded-xl bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)] flex items-center justify-center">
                <ShieldCheck size={16} />
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold">{u.name}</div>
                <div className="text-xs text-[color:var(--mano-ink-soft)]">{u.email}{u.phone ? " · " + u.phone : ""}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${u.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                {u.active ? "ACTIVE" : "DEACTIVATED"}
              </span>
              <button
                data-testid={`subadmin-toggle-${u.id}`}
                onClick={() => toggle(u)}
                className="px-3 py-1 rounded-full border border-[color:var(--mano-border)] text-xs font-semibold hover:bg-[color:var(--mano-bg)]"
              >
                {u.active ? "Deactivate" : "Reactivate"}
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <span className="uppercase-label">New sub-admin</span>
                <h2 className="font-display text-xl mt-1">Content manager account</h2>
              </div>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <p className="text-sm text-[color:var(--mano-ink-soft)] mt-2">
              Can add/edit/publish therapies and toggle visibility. Cannot approve doctors, view PHI, or manage other admins.
            </p>
            <div className="mt-4 space-y-3">
              <Field label="Full name">
                <input data-testid="sa-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inp} />
              </Field>
              <Field label="Email">
                <input data-testid="sa-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inp} />
              </Field>
              <Field label="Temporary password (min 6 chars)">
                <input data-testid="sa-password" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={inp} />
              </Field>
              <Field label="Phone (optional)">
                <input data-testid="sa-phone" type="tel" placeholder="+91…" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={inp} />
              </Field>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full border border-[color:var(--mano-border)] text-sm font-semibold">Cancel</button>
              <button
                data-testid="sa-create-btn"
                onClick={create}
                disabled={busy || !form.name || !form.email || form.password.length < 6}
                className="px-5 py-2 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-sm font-semibold disabled:opacity-60 transition-colors"
              >
                {busy ? "Creating…" : "Create sub-admin"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------ helpers ------------------------------
const inp = "mt-1 w-full h-11 px-3 rounded-lg border border-[color:var(--mano-border)] bg-white focus:outline-none focus:border-[color:var(--mano-primary)]";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[color:var(--mano-ink-soft)]">{label}</span>
      {children}
    </label>
  );
}
